import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Download } from "lucide-react";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import LastReviewed from "../components/LastReviewed";
import useLocalState from "../lib/useLocalState";
import { addCalendarMonth, addWorkingDays, fmtShort, daysUntil, parseIso } from "../lib/deadlines";

const OVERLINE_CLS = "text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold";
const LABEL_CLS = "block text-xs font-accent uppercase tracking-[0.14em] text-ink-muted mb-1.5";
const INPUT_CLS =
  "w-full bg-white border border-rule px-3 py-2.5 text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:border-ink transition-colors";

function todayLondon() {
  return new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function buildChase({ kind, name, address, la, ref, sentIso, situation, detail }) {
  const nameLine = name || "[Your full name]";
  const laLine = la || "[Local authority name]";
  const sentText = sentIso ? fmtShort(parseIso(sentIso)) : "[date you sent it]";
  const due = sentIso ? (kind === "sar" ? addCalendarMonth(sentIso) : addWorkingDays(sentIso, 20)) : null;
  const dueText = due ? fmtShort(due) : "[the statutory deadline]";
  const refLine = ref ? `\nCase / reference: ${ref}` : "";
  const addressBlock = address ? `${address.trim()}\n` : "";
  if (kind === "sar") {
    const what =
      situation === "partial"
        ? `On ${sentText} I made a subject access request for the personal data you hold about me. Your response was incomplete: ${detail || "[say briefly what is missing or wrongly withheld]"}.`
        : `On ${sentText} I made a subject access request for the personal data you hold about me. Under Article 12(3) UK GDPR you were required to respond within one calendar month — by ${dueText}. That deadline has passed and I have received no substantive response.`;
    return `From: ${nameLine}
${addressBlock}${refLine ? refLine.trim() + "\n" : ""}
To: The Data Protection Officer
${laLine}

Date: ${todayLondon()}

Dear Data Protection Officer,

Subject access request — formal complaint about handling

${what}

Please treat this letter as a formal complaint about the handling of my request. I ask that you:

  1. provide the requested data without further delay;
  2. where you rely on any exemption, identify — for each item withheld — the specific statutory exemption, the document to which it is applied, and your reasoning;
  3. confirm in writing the date by which I will receive your full response.

If I do not receive a substantive response within 14 days of this letter, I will complain to the Information Commissioner's Office and will provide them with a dated chronology of this request and copies of all correspondence.

Yours faithfully,

${nameLine}
`;
  }
  const what =
    situation === "partial"
      ? `On ${sentText} I made a request for recorded information under the Freedom of Information Act 2000. I am dissatisfied with your response: ${detail || "[say briefly what was refused or not addressed]"}.`
      : `On ${sentText} I made a request for recorded information under the Freedom of Information Act 2000. The statutory deadline of twenty working days — ${dueText} — has passed without a response.`;
  return `From: ${nameLine}
${addressBlock}
To: Freedom of Information / Information Governance Team
${laLine}

Date: ${todayLondon()}

Dear Information Rights Team,

Freedom of Information request — request for internal review

${what}

Please treat this letter as a formal request for an internal review of the handling of my request. In your review, please address:

  1. why the statutory deadline was not met, or why the information was withheld;
  2. for each item withheld, the specific exemption relied on and — where the exemption is qualified — your public interest reasoning;
  3. the date by which I will now receive the information or your review outcome.

If the internal review does not resolve this, I will complain to the Information Commissioner's Office under section 50 of the Act, enclosing a dated chronology and all correspondence.

Yours faithfully,

${nameLine}
`;
}

function buildIco({ kind, name, la, ref, sentIso, situation, detail }) {
  const nameLine = name || "[Your full name]";
  const laLine = la || "[Local authority name]";
  const sentText = sentIso ? fmtShort(parseIso(sentIso)) : "[date you sent it]";
  const due = sentIso ? (kind === "sar" ? addCalendarMonth(sentIso) : addWorkingDays(sentIso, 20)) : null;
  const dueText = due ? fmtShort(due) : "[the statutory deadline]";
  const kindName = kind === "sar" ? "subject access request" : "freedom of information request";
  const lawLine =
    kind === "sar"
      ? "Article 15 UK GDPR / Data Protection Act 2018 — response due within one calendar month (Article 12(3))."
      : "Freedom of Information Act 2000 — response due within twenty working days (section 10).";
  const natureLine =
    situation === "partial"
      ? `The organisation responded, but inadequately: ${detail || "[briefly: what was withheld or ignored, and what reason they gave]"}`
      : "The organisation has not provided a substantive response, despite the statutory deadline passing and a written chase.";
  return `COMPLAINT TO THE INFORMATION COMMISSIONER'S OFFICE
Submit at ico.org.uk/make-a-complaint or call 0303 123 1113.

Your name: ${nameLine}
Organisation complained about: ${laLine}${ref ? `\nReference: ${ref}` : ""}
Type of request: ${kindName}
Law engaged: ${lawLine}

Nature of complaint:
${natureLine}

Chronology:
  • ${sentText} — request sent in writing (copy available).
  • ${dueText} — statutory deadline passed.
  • ${todayLondon()} — chase letter / internal review request sent (copy available).

What I am asking the Commissioner to do:
Require the organisation to respond to my request in full, and — where anything is withheld — to identify the specific exemption applied to each document, with reasons.

I can provide copies of the original request, the chase letter and all correspondence.
`;
}

function LetterCard({ title, blurb, text, kind, filename }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // no-op
    }
  };
  const download = () => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return (
    <div className="border border-rule bg-white p-6 md:p-8">
      <div className={OVERLINE_CLS}>{blurb}</div>
      <h3 className="font-serif text-2xl md:text-3xl text-ink mt-2 mb-4 tracking-tight leading-tight">{title}</h3>
      <textarea
        readOnly
        value={text}
        data-testid={`complaint-${kind}-textarea`}
        rows={14}
        className="w-full bg-linen-alt border border-rule px-4 py-3 text-[13px] leading-relaxed font-mono text-ink resize-y focus:outline-none focus:border-ink"
        onFocus={(e) => e.target.select()}
      />
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={copy}
          data-testid={`complaint-${kind}-copy`}
          className="inline-flex items-center gap-2 border border-ink/30 text-ink px-4 py-2 text-xs font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
        >
          {copied ? <Check size={13} strokeWidth={2} /> : <Copy size={13} strokeWidth={1.75} />}
          {copied ? "Copied" : "Copy to clipboard"}
        </button>
        <button
          type="button"
          onClick={download}
          data-testid={`complaint-${kind}-download`}
          className="inline-flex items-center gap-2 bg-ink text-linen px-4 py-2 text-xs font-accent font-semibold tracking-wide hover:bg-river transition-colors"
        >
          <Download size={13} strokeWidth={1.75} />
          Download .txt
        </button>
      </div>
    </div>
  );
}

export default function ComplaintLetterPage() {
  const [tpl] = useLocalState("yana:tpl:v1", {
    name: "", address: "", dob: "", la: "", ref: "", extras: "", foiTopics: "",
  });
  const [deadlines] = useLocalState("yana:deadlines:v1", { sarSent: "", foiSent: "" });
  const [kind, setKind] = useState("sar");
  const [sentOverride, setSentOverride] = useState("");
  const [situation, setSituation] = useState("none");
  const [detail, setDetail] = useState("");

  const sentIso = sentOverride || (kind === "sar" ? deadlines.sarSent : deadlines.foiSent);
  const due = sentIso ? (kind === "sar" ? addCalendarMonth(sentIso) : addWorkingDays(sentIso, 20)) : null;
  const overdueDays = due ? -daysUntil(due) : null;

  const args = { kind, name: tpl.name, address: tpl.address, la: tpl.la, ref: tpl.ref, sentIso, situation, detail };
  const chase = useMemo(() => buildChase(args), [kind, tpl.name, tpl.address, tpl.la, tpl.ref, sentIso, situation, detail]); // eslint-disable-line react-hooks/exhaustive-deps
  const ico = useMemo(() => buildIco(args), [kind, tpl.name, tpl.la, tpl.ref, sentIso, situation, detail]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="complaint-page">
      <PageMeta
        title="Missed deadline? Chase and ICO complaint letters, ready to send"
        description="If your subject access request or FOI request is overdue, this builds a ready-to-send chase letter and an ICO complaint — pre-filled with your saved details and dates."
        path="/understanding/records/complaint"
      />
      <PageHeader
        overline="Records · When they don't reply"
        title="They missed the deadline. Now what?"
        lede="A missed deadline is not the end of the road — it is the start of the paper trail that gets results. Two letters, built from the details and dates you have already saved: a firm chase to the organisation, and a complaint ready for the Information Commissioner."
      />

      <article className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
        <div className="flex items-center justify-between gap-4 mb-10 flex-wrap">
          <Link
            to="/understanding/records/templates"
            data-testid="complaint-back-link"
            className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back to the templates &amp; deadline tracker
          </Link>
          <LastReviewed path="/understanding/records/complaint" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <div className="border border-rule bg-linen-alt p-6 md:p-7 space-y-5">
              <div>
                <div className={OVERLINE_CLS}>Which request?</div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    data-testid="complaint-kind-sar"
                    onClick={() => { setKind("sar"); setSentOverride(""); }}
                    className={`px-4 py-2 text-xs font-accent font-semibold tracking-wide border transition-colors ${kind === "sar" ? "bg-ink text-linen border-ink" : "border-rule text-ink-muted hover:border-ink hover:text-ink"}`}
                  >
                    SAR
                  </button>
                  <button
                    type="button"
                    data-testid="complaint-kind-foi"
                    onClick={() => { setKind("foi"); setSentOverride(""); }}
                    className={`px-4 py-2 text-xs font-accent font-semibold tracking-wide border transition-colors ${kind === "foi" ? "bg-ink text-linen border-ink" : "border-rule text-ink-muted hover:border-ink hover:text-ink"}`}
                  >
                    FOI
                  </button>
                </div>
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="complaint-sent">Date you sent the request</label>
                <input
                  id="complaint-sent"
                  data-testid="complaint-sent-date"
                  type="date"
                  value={sentIso || ""}
                  onChange={(e) => setSentOverride(e.target.value)}
                  className={INPUT_CLS + " w-auto"}
                />
                {due && (
                  <p data-testid="complaint-deadline-line" className="mt-2 text-xs font-accent text-ink-muted leading-relaxed">
                    Deadline was <strong className="text-ink">{fmtShort(due)}</strong>
                    {overdueDays > 0 && (
                      <> — <span className="text-ember font-semibold">{overdueDays} {overdueDays === 1 ? "day" : "days"} overdue</span></>
                    )}
                    {overdueDays <= 0 && <> — not yet overdue; a chase is still reasonable if promised sooner.</>}
                  </p>
                )}
              </div>
              <div>
                <div className={LABEL_CLS.replace("mb-1.5", "mb-2")}>What happened?</div>
                <label className="flex items-start gap-2 text-sm text-ink-muted cursor-pointer">
                  <input
                    type="radio"
                    name="situation"
                    data-testid="complaint-situation-none"
                    checked={situation === "none"}
                    onChange={() => setSituation("none")}
                    className="mt-1"
                  />
                  <span>No reply at all</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-ink-muted cursor-pointer mt-2">
                  <input
                    type="radio"
                    name="situation"
                    data-testid="complaint-situation-partial"
                    checked={situation === "partial"}
                    onChange={() => setSituation("partial")}
                    className="mt-1"
                  />
                  <span>A reply came, but it was incomplete or a refusal</span>
                </label>
              </div>
              {situation === "partial" && (
                <div>
                  <label className={LABEL_CLS} htmlFor="complaint-detail">Briefly — what is wrong with their reply?</label>
                  <textarea
                    id="complaint-detail"
                    data-testid="complaint-detail"
                    className={INPUT_CLS}
                    rows={3}
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    placeholder="e.g. whole documents redacted citing third-party data; no assessments included"
                  />
                </div>
              )}
              <p className="text-[11px] font-accent text-ink-muted leading-relaxed">
                Your name, address and local authority come from the details you saved on the{" "}
                <Link to="/understanding/records/templates" className="underline">templates page</Link>{" "}
                — fill them in there and they appear here. Everything stays in this browser.
              </p>
            </div>
          </aside>

          <div className="lg:col-span-8 space-y-8">
            <LetterCard
              title={kind === "sar" ? "Chase letter — to their Data Protection Officer" : "Internal review request — to their FOI team"}
              blurb="Step 1 · Send this first"
              text={chase}
              kind="chase"
              filename={kind === "sar" ? "sar-chase-letter.txt" : "foi-internal-review-request.txt"}
            />
            <LetterCard
              title="Complaint to the Information Commissioner"
              blurb="Step 2 · If 14 days pass with nothing"
              text={ico}
              kind="ico"
              filename={`${kind}-ico-complaint.txt`}
            />
            <div className="border border-rule bg-linen-alt p-6 md:p-8 text-sm text-ink-muted leading-relaxed">
              <div className={OVERLINE_CLS}>The order matters</div>
              <p className="mt-3">
                The ICO expects you to have chased the organisation first — for FOI, that means asking
                for an <strong className="text-ink">internal review</strong> before complaining. Send the
                chase letter, diarise 14 days, and keep copies of everything. Dates and copies are what
                move a regulator; frustration, however justified, is not.
              </p>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
